/**
 * Quotation and pricing arithmetic.
 *
 * ALL MONEY IS AN INTEGER NUMBER OF MINOR UNITS. Nothing in this file ever
 * produces a fractional minor unit, and nothing here adds two floating-point
 * amounts. That is the whole point: an invoice total that disagrees with the
 * sum of its lines by a halala is a dispute with a client, and floating-point
 * addition produces exactly that.
 *
 * The BACKEND is the authority. These functions are shared so the interface
 * can show a running total while somebody types, but every stored figure is
 * recomputed on the server from the stored inputs before it is written. The
 * client never sends a total.
 *
 * ROUNDING. Percentages — a line discount, a document discount, the tax — are
 * applied to an integer amount and rounded HALF UP to the nearest minor unit.
 * Half-up is stated here, applied in one place, and documented in
 * `API_DOCUMENTATION.md`, because a rounding rule that is implied rather than
 * written down is a rounding rule that two implementations will disagree on.
 *
 * NONE OF THIS IS TEST SCIENCE. It is commercial arithmetic on money, of the
 * kind any invoice performs. The rounding of a RESULT — significant figures,
 * a reported precision, a specification limit — is a different thing entirely
 * and is gated behind CLAUDE.md section 4. Nothing here touches it.
 */
// ---------------------------------------------------------------------------
// Quotation lifecycle
// ---------------------------------------------------------------------------
/**
 * The lifecycle states a quotation is STORED in.
 *
 * `expired` is deliberately absent. Expiry is not an event anybody performs,
 * it is the passage of a date, and storing it would create a second source of
 * truth that a background job would have to keep agreeing with the validity
 * date. Instead the validity date is the single fact and expiry is DERIVED —
 * see `isQuotationExpired` and `effectiveQuotationStatus`. `QUOTATION_STATUS`
 * in `status.ts` is the full set including the derived state, and is what the
 * interface renders.
 */
export const QUOTATION_STORED_STATUS = {
    DRAFT: 'draft',
    SENT: 'sent',
    /** Set ONLY by recording the client's actual response. Printing is not it. */
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
};
export const ALL_QUOTATION_STORED_STATUSES = Object.freeze(Object.values(QUOTATION_STORED_STATUS));
/**
 * The transitions the product permits, and the only ones.
 *
 * Note what cannot happen: nothing returns to `draft` once the client has been
 * given a document, and `accepted` and `rejected` are terminal. A quotation
 * whose terms need to change after it was sent becomes a NEW quotation with a
 * new number, because the old one is what the client is holding.
 */
export const QUOTATION_TRANSITIONS = Object.freeze({
    [QUOTATION_STORED_STATUS.DRAFT]: Object.freeze([
        QUOTATION_STORED_STATUS.SENT,
    ]),
    [QUOTATION_STORED_STATUS.SENT]: Object.freeze([
        QUOTATION_STORED_STATUS.ACCEPTED,
        QUOTATION_STORED_STATUS.REJECTED,
    ]),
    [QUOTATION_STORED_STATUS.ACCEPTED]: Object.freeze([]),
    [QUOTATION_STORED_STATUS.REJECTED]: Object.freeze([]),
});
export function quotationTransitionAllowed(from, to) {
    return QUOTATION_TRANSITIONS[from].includes(to);
}
/**
 * Is this quotation past its validity date?
 *
 * Compared as calendar DATES in the laboratory's own day, not as instants: a
 * quotation valid until the 30th is valid all of the 30th. `today` is supplied
 * by the caller — the backend passes the laboratory's timezone-local date, so
 * a quotation does not expire at nine in the evening because the server is on
 * UTC.
 */
export function isQuotationExpired(status, validUntil, today) {
    if (status !== QUOTATION_STORED_STATUS.SENT)
        return false;
    return validUntil < today;
}
/**
 * What to SHOW: the stored status, unless the validity date has passed.
 *
 * A quotation that was accepted or rejected keeps that status forever. Expiry
 * only ever describes one that was sent and never answered.
 */
export function effectiveQuotationStatus(status, validUntil, today) {
    return isQuotationExpired(status, validUntil, today) ? 'expired' : status;
}
/** Only a draft may have its services, quantities or prices changed. */
export function quotationIsEditable(status) {
    return status === QUOTATION_STORED_STATUS.DRAFT;
}
// ---------------------------------------------------------------------------
// Where a price came from
// ---------------------------------------------------------------------------
/**
 * The provenance of the price actually charged on a line.
 *
 * Recorded per line rather than inferred, because "this was the catalogue
 * price at the time" and "somebody with permission changed it, and here is
 * why" are different facts, and six months later nobody can reconstruct which
 * one it was from the number alone.
 */
export const PRICE_SOURCE = {
    /** The catalogue's base price at the moment the line was priced. */
    CATALOGUE: 'catalogue',
    /** A price agreed with this client in a pricing agreement. */
    AGREEMENT: 'agreement',
    /** Deliberately different. Requires `pricing.override` AND a reason. */
    OVERRIDE: 'override',
};
export const ALL_PRICE_SOURCES = Object.freeze(Object.values(PRICE_SOURCE));
/** How a quotation was actually put into the client's hands. */
export const QUOTATION_SEND_CHANNEL = {
    /** Printed or handed over in person. */
    HANDED_OVER: 'handed_over',
    /** Emailed BY THE USER, from their own mail client. Not by this system. */
    EMAIL_BY_USER: 'email_by_user',
    OTHER: 'other',
};
export const ALL_QUOTATION_SEND_CHANNELS = Object.freeze(Object.values(QUOTATION_SEND_CHANNEL));
// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------
/**
 * Round half up to a whole minor unit.
 *
 * `Math.round` rounds half towards positive infinity, which is half-up for the
 * non-negative amounts this system deals in. It is wrapped anyway so the rule
 * is named, tested and changed in exactly one place.
 */
export function roundMinor(value) {
    return Math.round(value);
}
export function discountFor(amountMinor, discount) {
    if (!discount)
        return 0;
    const percent = discount.percent ?? null;
    const fixed = discount.amountMinor ?? null;
    // An ABSENT discount is not a discount of zero, and both being absent is the
    // ordinary case — most lines are not discounted at all.
    if (percent === null && fixed === null)
        return 0;
    if (percent !== null && fixed !== null) {
        throw new Error('A discount is either a percentage or an amount, never both.');
    }
    const raw = percent !== null ? roundMinor((amountMinor * percent) / 100) : (fixed ?? 0);
    return Math.min(Math.max(raw, 0), Math.max(amountMinor, 0));
}
export function computeLineTotals(line) {
    const subtotalMinor = roundMinor(line.quantity * line.unitPriceMinor);
    const discountMinor = discountFor(subtotalMinor, line.discount);
    return {
        subtotalMinor,
        discountMinor,
        totalMinor: subtotalMinor - discountMinor,
    };
}
/**
 * The whole document.
 *
 * ORDER MATTERS AND IS FIXED: line discounts first, then the document
 * discount on what remains, then tax on what remains after that. Taxing before
 * discounting would overstate the tax; discounting the tax-inclusive total
 * would understate it. The order is stated in the API documentation and on the
 * quotation itself, where a client can check the arithmetic.
 */
export function computeQuotationTotals(input) {
    let subtotalMinor = 0;
    let lineDiscountMinor = 0;
    for (const line of input.lines) {
        const totals = computeLineTotals(line);
        subtotalMinor += totals.subtotalMinor;
        lineDiscountMinor += totals.discountMinor;
    }
    const afterLines = subtotalMinor - lineDiscountMinor;
    const documentDiscountMinor = discountFor(afterLines, input.documentDiscount);
    const taxableMinor = afterLines - documentDiscountMinor;
    const taxMinor = roundMinor((taxableMinor * input.taxRatePercent) / 100);
    return {
        subtotalMinor,
        lineDiscountMinor,
        documentDiscountMinor,
        discountMinor: lineDiscountMinor + documentDiscountMinor,
        taxableMinor,
        taxMinor,
        totalMinor: taxableMinor + taxMinor,
    };
}
// ---------------------------------------------------------------------------
// Pricing agreements
// ---------------------------------------------------------------------------
/**
 * Is this agreement in force on a given day?
 *
 * Both bounds are inclusive calendar dates, and a null end date means "until
 * cancelled", which is how most laboratories actually write one.
 */
export function agreementIsEffectiveOn(agreement, onDate) {
    if (!agreement.isActive)
        return false;
    if (onDate < agreement.validFrom)
        return false;
    if (agreement.validUntil !== null && onDate > agreement.validUntil) {
        return false;
    }
    return true;
}
export function resolvePrice(input) {
    if (input.agreementPriceMinor !== null) {
        return {
            priceMinor: input.agreementPriceMinor,
            source: PRICE_SOURCE.AGREEMENT,
        };
    }
    if (input.basePriceMinor !== null) {
        return { priceMinor: input.basePriceMinor, source: PRICE_SOURCE.CATALOGUE };
    }
    return { priceMinor: null, source: null };
}
//# sourceMappingURL=pricing.js.map